import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Box, Typography, Button, Alert, CircularProgress } from '@mui/material';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import { Map as MapIcon, Launch as LaunchIcon } from '@mui/icons-material';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png'),
  iconUrl: require('leaflet/dist/images/marker-icon.png'),
  shadowUrl: require('leaflet/dist/images/marker-shadow.png'),
});

// Global cache for geocoding results to avoid repeated API calls
const geocodingCache = new Map();

// Rate limiting: Track last request time to ensure 1 request per second max
let lastRequestTime = 0;
const MIN_REQUEST_INTERVAL = 1100; // 1.1 seconds to be safe

const OpportunityMapSection = ({ opportunity, company, contacts }) => {
  const [coordinates, setCoordinates] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [addressSource, setAddressSource] = useState(null); // Track which source provided the address
  const lastGeocodedAddress = useRef(null); // Track last geocoded address to prevent duplicate calls

  // Get address with priority: company first, then contacts in order
  const getAddressData = () => {
    // Priority 1: Company address (if company is assigned and has valid address)
    // Note: When used on Contact page, company represents the contact itself, so companyId check is not needed
    if (company && hasValidAddress(company)) {
      return {
        source: 'company',
        data: {
          street1: company.street1,
          street2: company.street2,
          city: company.city,
          province: company.province,
          country: company.country,
          postalCode: company.postalCode
        },
        name: company.companyName
      };
    }

    // Priority 2: Contact addresses (in order from top to bottom)
    if (contacts && contacts.length > 0) {
      // Ensure contacts are in the same order as in the opportunity
      const orderedContacts = (opportunity?.contactIds || [])
        .map(id => contacts.find(c => 
          c.contactId && id && 
          c.contactId.toLowerCase() === id.toLowerCase()
        ))
        .filter(contact => contact && hasValidAddress(contact));

      if (orderedContacts.length > 0) {
        const firstValidContact = orderedContacts[0];
        return {
          source: 'contact',
          data: {
            street1: firstValidContact.street1 || firstValidContact.Street1,
            street2: firstValidContact.street2 || firstValidContact.Street2,
            city: firstValidContact.city || firstValidContact.City,
            province: firstValidContact.province || firstValidContact.Province,
            country: firstValidContact.country || firstValidContact.Country,
            postalCode: firstValidContact.postalCode || firstValidContact.PostalCode
          },
          name: `${firstValidContact.firstName || firstValidContact.FirstName} ${firstValidContact.lastName || firstValidContact.LastName}`.trim()
        };
      }
    }

    // No valid address found
    return null;
  };

  // Check if an entity has a valid address
  const hasValidAddress = (entity) => {
    if (!entity) return false;
    
    // Check for required address fields (at least one of these must be present)
    const hasStreet = Boolean(entity.street1?.trim());
    const hasCity = Boolean(entity.city?.trim());
    const hasProvince = Boolean(entity.province?.trim());
    const hasCountry = Boolean(entity.country?.trim());
    
    // Consider an address valid if it has either:
    // 1. Street + (City or Province or Country), or
    // 2. City + (Province or Country)
    return (hasStreet && (hasCity || hasProvince || hasCountry)) ||
           (hasCity && (hasProvince || hasCountry));
  };

  // Build address string for display and geocoding (excluding postal code for OpenStreetMap)
  const buildAddress = (entity, includePostalCode = true) => {
    if (!entity) return '';
    const parts = [];
    if (entity.street1) parts.push(entity.street1);
    if (entity.street2) parts.push(entity.street2);
    if (entity.city) parts.push(entity.city);
    if (entity.province) parts.push(entity.province);
    if (entity.country) parts.push(entity.country);
    if (includePostalCode && entity.postalCode) parts.push(entity.postalCode);
    return parts.join(', ');
  };

  // Build address for geocoding (exclude Street 2 and Postal Code for better OpenStreetMap results)
  const buildGeocodingAddress = (entity) => {
    if (!entity) return '';
    const parts = [];
    if (entity.street1) parts.push(entity.street1);
    // Skip street2 for geocoding
    if (entity.city) parts.push(entity.city);
    if (entity.province) parts.push(entity.province);
    if (entity.country) parts.push(entity.country);
    // Skip postalCode for geocoding
    return parts.join(', ');
  };

  const addressData = getAddressData();
  const fullAddress = addressData ? buildAddress(addressData.data, true) : '';
  const geocodingAddress = addressData ? buildGeocodingAddress(addressData.data) : '';

  const geocodeAddress = useCallback(async (address) => {
    if (!address || !address.trim()) {
      setError('No address available for mapping');
      return;
    }

    // Check cache first
    const cacheKey = address.toLowerCase().trim();
    if (geocodingCache.has(cacheKey)) {
      const cachedResult = geocodingCache.get(cacheKey);
      if (cachedResult.coordinates) {
        setCoordinates(cachedResult.coordinates);
        setAddressSource(addressData);
        setError('');
      } else {
        setError(cachedResult.error || 'Address not found on map');
        setCoordinates(null);
      }
      return;
    }

    setLoading(true);
    setError('');
    setCoordinates(null);
    setAddressSource(addressData);

    try {
      // Rate limiting: Ensure we don't exceed 1 request per second
      const now = Date.now();
      const timeSinceLastRequest = now - lastRequestTime;
      if (timeSinceLastRequest < MIN_REQUEST_INTERVAL) {
        const waitTime = MIN_REQUEST_INTERVAL - timeSinceLastRequest;
        await new Promise(resolve => setTimeout(resolve, waitTime));
      }
      lastRequestTime = Date.now();

      // Create AbortController for timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 8000); // Reduced to 8 seconds

      // Use OpenStreetMap Nominatim API for geocoding with proper headers
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(address)}&limit=1`,
        {
          signal: controller.signal,
          headers: {
            'User-Agent': 'PulseCRM/1.0 (Business CRM Application)', // Required by Nominatim policy
          }
        }
      );
      
      clearTimeout(timeoutId);
      
      if (!response.ok) {
        throw new Error(`Geocoding service error: ${response.status}`);
      }

      const data = await response.json();
      
      if (data && data.length > 0) {
        const { lat, lon } = data[0];
        const coordinates = [parseFloat(lat), parseFloat(lon)];
        
        // Cache successful result
        geocodingCache.set(cacheKey, { coordinates });
        
        setCoordinates(coordinates);
        setError('');
      } else {
        // Cache failed result to avoid retrying
        const errorMsg = 'Address not found on map';
        geocodingCache.set(cacheKey, { error: errorMsg });
        setError(errorMsg);
      }
    } catch (err) {
      console.error('Geocoding error:', err);
      
      let errorMessage = 'Unable to locate address on map';
      if (err.name === 'AbortError') {
        errorMessage = 'Map loading timed out. Please try again later.';
      } else if (err.message.includes('429')) {
        errorMessage = 'Too many map requests. Please wait a moment and try again.';
      } else if (err.message.includes('503') || err.message.includes('502')) {
        errorMessage = 'Map service temporarily unavailable. Please try again later.';
      }
      
      // Cache error to avoid immediate retry
      geocodingCache.set(cacheKey, { error: errorMessage });
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [addressData]);

  useEffect(() => {
    // Only geocode if we have an address and it's different from the last one
    if (geocodingAddress && geocodingAddress !== lastGeocodedAddress.current) {
      lastGeocodedAddress.current = geocodingAddress;
      geocodeAddress(geocodingAddress);
    } else if (!geocodingAddress) {
      setCoordinates(null);
      setError('');
      setAddressSource(null);
      lastGeocodedAddress.current = null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [geocodingAddress]); // Removed geocodeAddress from dependencies to prevent infinite loop

  // Early return after all hooks are called
  if (!addressData) {
    return null;
  }

  const handleGoogleMapsClick = () => {
    if (fullAddress) {
      const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(fullAddress)}`;
      window.open(googleMapsUrl, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center' }}>
          <MapIcon sx={{ mr: 1 }} />
          Location
        </Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<LaunchIcon />}
          onClick={handleGoogleMapsClick}
          disabled={!fullAddress}
        >
          Link to Google Maps
        </Button>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
        {fullAddress}
      </Typography>
      
      {loading && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <CircularProgress size={16} />
          <Typography variant="body2">Loading map...</Typography>
        </Box>
      )}

      {error && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {coordinates && (
        <Box sx={{ height: 300, width: '100%', borderRadius: 1, overflow: 'hidden' }}>
          <MapContainer
            center={coordinates}
            zoom={15}
            style={{ height: '100%', width: '100%' }}
          >
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            <Marker position={coordinates}>
              <Popup>
                <div>
                  <strong>{addressSource.name}</strong>
                  <br />
                  {fullAddress}
                </div>
              </Popup>
            </Marker>
          </MapContainer>
        </Box>
      )}
    </Box>
  );
};

export default OpportunityMapSection; 